import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/context/LangContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function InfiniteCarousel({ logos, direction = "left", speed = 0.4 }) {
  const trackRef = useRef(null);
  const rafRef = useRef(null);
  const posRef = useRef(0);
  const pausedRef = useRef(false);

  useEffect(() => {
    const track = trackRef.current;
    if (!track || logos.length === 0) return;

    const animate = () => {
      if (!pausedRef.current) {
        posRef.current += direction === "left" ? -speed : speed;
        const totalWidth = track.scrollWidth / 2;
        if (direction === "left" && posRef.current <= -totalWidth) posRef.current = 0;
        if (direction === "right" && posRef.current >= 0) posRef.current = -totalWidth;
        track.style.transform = `translateX(${posRef.current}px)`;
      }
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [logos, direction, speed]);

  if (logos.length === 0) return null;
  const tripled = [...logos, ...logos, ...logos];

  return (
    <div
      className="overflow-hidden py-5 md:py-6 group/track"
      onMouseEnter={() => { pausedRef.current = true; }}
      onMouseLeave={() => { pausedRef.current = false; }}
    >
      <div ref={trackRef} className="flex items-center gap-6 md:gap-8 w-max will-change-transform">
        {tripled.map((logo, i) => (
          <div
            key={`${logo.id || i}-${i}`}
            className="shrink-0 relative group/logo"
          >
            {/* Card container - clickable if URL exists */}
            <a
              href={logo.url || undefined}
              target={logo.url ? "_blank" : undefined}
              rel={logo.url ? "noopener noreferrer" : undefined}
              className={`block w-44 h-28 sm:w-52 sm:h-32 md:w-60 md:h-36 flex items-center justify-center px-5 py-4 border border-white/[0.06] bg-white/[0.02] hover:border-[#E60000]/30 hover:bg-[#E60000]/[0.03] rounded-sm transition-all duration-500 relative overflow-hidden ${logo.url ? "cursor-pointer" : ""}`}
            >
              {/* Subtle corner accent */}
              <div className="absolute top-0 left-0 w-6 h-[2px] bg-[#E60000]/0 group-hover/logo:bg-[#E60000]/40 transition-all duration-500" />
              <div className="absolute top-0 left-0 w-[2px] h-6 bg-[#E60000]/0 group-hover/logo:bg-[#E60000]/40 transition-all duration-500" />

              <img
                src={logo.image}
                alt={logo.title || "Partenaire"}
                className="max-h-full max-w-full object-contain grayscale opacity-50 group-hover/logo:grayscale-0 group-hover/logo:opacity-100 transition-all duration-500 group-hover/logo:scale-105"
              />
            </a>
            {/* Name always visible below */}
            {logo.title && (
              <p className="text-center mt-2.5 font-body text-[11px] sm:text-xs text-white/30 group-hover/logo:text-white/70 uppercase tracking-widest transition-colors duration-500 truncate max-w-[15rem]">
                {logo.title}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Partners() {
  const { lang } = useLang();
  const [logos, setLogos] = useState([]);

  useEffect(() => {
    axios.get(`${API}/content/partners`)
      .then(r => setLogos(r.data || []))
      .catch(e => console.error("Partners fetch error:", e));
  }, []);

  if (logos.length === 0) return null;

  const half = Math.ceil(logos.length / 2);
  const row1 = logos.slice(0, half);
  const row2 = logos.slice(half);

  return (
    <section className="relative py-16 md:py-24 bg-[#050505] overflow-hidden" data-testid="partners-section">
      {/* Top line */}
      <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-[#E60000]/15 to-transparent" />

      {/* Background glow */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-[#E60000]/[0.03] rounded-full blur-[120px]" />

      <div className="relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 md:mb-14 px-4"
        >
          <span className="text-[#E60000] text-xs sm:text-sm uppercase tracking-[0.3em] font-medium font-body">
            {lang === "fr" ? "Ils nous font confiance" : "They trust us"}
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl tracking-tight font-bold uppercase text-white mt-3" data-testid="partners-title">
            {lang === "fr" ? "Nos" : "Our"} <span className="text-[#E60000]">{lang === "fr" ? "partenaires." : "partners."}</span>
          </h2>
          <p className="font-body text-sm text-white/30 mt-3 max-w-md mx-auto">
            {lang === "fr"
              ? "Commanditaires, événements et collaborations qui définissent notre parcours."
              : "Sponsors, events and collaborations that define our journey."}
          </p>
        </motion.div>

        {/* Fade edges */}
        <div className="relative">
          <div className="absolute left-0 top-0 bottom-0 w-24 md:w-40 bg-gradient-to-r from-[#050505] to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-24 md:w-40 bg-gradient-to-l from-[#050505] to-transparent z-10 pointer-events-none" />

          {/* Row 1 - left */}
          <InfiniteCarousel logos={row1} direction="left" speed={0.35} />

          {/* Row 2 - right (only if enough logos) */}
          {row2.length > 0 && <InfiniteCarousel logos={row2} direction="right" speed={0.25} />}
        </div>
      </div>

      {/* Bottom line */}
      <div className="absolute bottom-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-[#E60000]/15 to-transparent" />
    </section>
  );
}
